/**
 * Tema tercihi — açık / koyu / sistem.
 *
 * =============================================================================
 * TEK KAYNAK, ÜÇ İSTEMCİ
 * -----------------------------------------------------------------------------
 * Renk basamakları zaten `tokens.css` içinde hem açık hem koyu için tanımlı;
 * eksik olan tek şey KULLANICI SEÇİMİYDİ. Bu modül o seçimi tutar ve kök
 * elemana yazar. `data-tema` KULLANICI SEÇİMİDİR ve sistem tercihini EZER
 * (tasarım sistemi §3); "sistem" seçildiğinde öznitelik kaldırılır ve
 * prefers-color-scheme yeniden yetkili olur.
 *
 * Neden Zustand değil: bu paket React dışında bir durum kütüphanesine bağlı
 * değildir ve öyle kalmalı. useSyncExternalStore bu iş için yeterli.
 *
 * PWA NOTU: veli ve kasa ana ekrana eklenebilir. Kurulu bir PWA'da durum
 * çubuğunun rengini `<meta name="theme-color">` belirler; index.html'deki
 * media'lı etiketler yalnızca SİSTEM tercihini bilir. Kullanıcı açık temayı
 * elle seçtiğinde durum çubuğu koyu kalmasın diye, seçim yapıldığında
 * media'sız bir etiket head'in EN BAŞINA eklenir — tarayıcı eşleşen ilk
 * etiketi kullanır, bu yüzden sıra önemlidir.
 * =============================================================================
 */

export type Tema = "sistem" | "acik" | "koyu";

/** Düğme sırası: sistem → açık → koyu → sistem. */
const SIRA: readonly Tema[] = ["sistem", "acik", "koyu"] as const;

/** Durum çubuğu renkleri — tokens.css'teki --kagit ile birebir aynı. */
const ZEMIN: Record<"acik" | "koyu", string> = {
  acik: "#EDEDEE",
  koyu: "#08090A",
};

const VARSAYILAN_ANAHTAR = "kantin.tema";

let anahtar = VARSAYILAN_ANAHTAR;
let mevcut: Tema = "sistem";
const aboneler = new Set<() => void>();
let sistemDinleyicisiKuruldu = false;

function gecerli(d: unknown): d is Tema {
  return d === "sistem" || d === "acik" || d === "koyu";
}

function depodanOku(): Tema {
  try {
    const ham = localStorage.getItem(anahtar);
    return gecerli(ham) ? ham : "sistem";
  } catch {
    // Depolama kapalıysa (gizli sekme, kısıtlı WebView) tema bu sekmede yaşar.
    return "sistem";
  }
}

/** Sıradaki tema — düğme her tıklamada bir basamak ilerler. */
export function sonrakiTema(t: Tema): Tema {
  const i = SIRA.indexOf(t);
  return SIRA[(i + 1) % SIRA.length]!;
}

/** Ekran okuyucu ve ipucu metni. */
export const TEMA_METNI: Record<Tema, string> = {
  sistem: "Tema: sistem",
  acik: "Tema: açık",
  koyu: "Tema: koyu",
};

/** Kurulu PWA'da durum çubuğu rengini seçime göre ezer. */
function durumCubuguRengi(tema: Tema): void {
  if (typeof document === "undefined") return;
  const eski = document.head.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"][data-tema-rengi]',
  );
  if (tema === "sistem") {
    // Seçim kalkınca index.html'deki media'lı etiketler yeniden yetkili olur.
    eski?.remove();
    return;
  }
  const etiket = eski ?? document.createElement("meta");
  etiket.name = "theme-color";
  etiket.dataset.temaRengi = "";
  etiket.content = ZEMIN[tema];
  // Media'sız etiket HER ZAMAN eşleşir; kazanması için en başta durmalı.
  if (!eski) document.head.prepend(etiket);
}

/** Temayı kök elemana yazar. */
export function temayiUygula(tema: Tema): void {
  if (typeof document === "undefined") return;
  const kok = document.documentElement;
  if (tema === "sistem") delete kok.dataset.tema;
  else kok.dataset.tema = tema;
  durumCubuguRengi(tema);
}

/**
 * Uygulama açılışında, İLK BOYAMADAN ÖNCE çağrılır: aksi halde koyu temayı
 * seçen kullanıcı her açılışta bir kare beyaz görür.
 *
 * @param depoAnahtari uygulamaya özel localStorage anahtarı
 */
export function temaBaslat(depoAnahtari: string): void {
  anahtar = depoAnahtari;
  mevcut = depodanOku();
  temayiUygula(mevcut);

  // "sistem" seçiliyken kullanıcı işletim sistemini değiştirirse renkler CSS
  // ile kendiliğinden döner; durum çubuğunu da senkron tutmak gerekir.
  if (!sistemDinleyicisiKuruldu && typeof window !== "undefined") {
    sistemDinleyicisiKuruldu = true;
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => {
        if (mevcut === "sistem") temayiUygula("sistem");
        aboneler.forEach((f) => f());
      });
  }
}

export function temaOku(): Tema {
  return mevcut;
}

export function temaAyarla(tema: Tema): void {
  if (tema === mevcut) return;
  mevcut = tema;
  try {
    localStorage.setItem(anahtar, tema);
  } catch {
    /* depolama kapalıysa seçim yalnızca bu sekmede yaşar */
  }
  temayiUygula(tema);
  aboneler.forEach((f) => f());
}

export function temaAbone(dinleyici: () => void): () => void {
  aboneler.add(dinleyici);
  return () => {
    aboneler.delete(dinleyici);
  };
}
